import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ScriptService {
  #document = inject(DOCUMENT);
  #scriptsLoaders: {
    [url: string]: Observable<Event>;
  } = {};

  #loadScript(
    url: string,
    attributes?: { [s: string]: string },
    targetEl: HTMLElement | string = 'head',
  ): Observable<Event> {
    return new Observable<Event>((observer) => {
      const script: HTMLScriptElement = this.#document.createElement('script');

      if (attributes) {
        for (const key in attributes) {
          if (Object.prototype.hasOwnProperty.call(attributes, key)) {
            script.setAttribute(key, attributes[key]);
          }
        }
      }

      script.onload = (event: Event) => {
        observer.next(event);
        observer.complete();
      };

      script.onerror = (err) => {
        observer.error(err);
      };

      script.src = url;

      const targetElement: HTMLElement | null =
        typeof targetEl === 'string'
          ? this.#document.querySelector(targetEl)
          : targetEl;
      if (targetElement === null) {
        throw new Error(
          'ScriptService: Target element for appending script does not exist!',
        );
      }
      targetElement.appendChild(script);
    });
  }

  /**
   * Injects script from given url to target place in DOM
   * This method loads script from same url once.
   *
   * @param url Url of the external script to be loaded
   * @param attributes Attribute list to be added to the script element
   * @param targetEl Target element for the placing script tag. It can be a selector or a element reference
   */
  loadScript(
    url: string,
    attributes?: { [s: string]: string },
    targetEl: HTMLElement | string = 'head',
  ): Observable<Event> {
    return (this.#scriptsLoaders[url] =
      this.#scriptsLoaders[url] ||
      this.#loadScript(url, attributes, targetEl).pipe(
        take(1),
        shareReplay(1),
      ));
  }
}
